import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Card, Table, Tag, Space, Button, Tooltip, Typography, Popconfirm, Modal, Checkbox, Select, Collapse, message, Alert,
} from 'antd'
import {
  QuestionCircleOutlined, ReloadOutlined, ScanOutlined, CloudSyncOutlined, InfoCircleOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import componentService, { componentLabel, errMsg } from '../../services/componentService'
import BatchProgressModal, { BATCH_STATUS, BATCH_PHASE } from './BatchProgressModal'

const { Text, Paragraph } = Typography

const formatSize = (b) => {
  if (!b) return '—'
  if (b < 1024) return `${b} B`
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`
  return `${(b / 1024 / 1024).toFixed(1)} MB`
}

// ---------------- 批量更新弹窗 ----------------
const BatchUpdateModal = ({ comp, open, onClose, onStarted }) => {
  const [nodes, setNodes] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState([])
  const [canary, setCanary] = useState(true)
  const [force, setForce] = useState(false)
  const [version, setVersion] = useState(undefined)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!open || !comp) return
    setCanary(true)
    setForce(false)
    setVersion(undefined)
    setLoading(true)
    componentService
      .listAllNodes()
      .then((res) => {
        const raw = res.data
        const all = Array.isArray(raw) ? raw : raw?.nodes || []
        const types = comp.business_types // null = 所有节点
        const rows = []
        all.forEach((n) => {
          const c = (n.components || []).find((x) => x.component === comp.component)
          const typeMatch = !types || types.includes(n.business_type) || n.business_type === 'mixed'
          if (c || typeMatch) {
            rows.push({ id: n.id, name: n.name, ip: n.ip_address, business_type: n.business_type, comp: c || null })
          }
        })
        // 过期的排前面
        rows.sort((a, b) => (b.comp?.outdated === true) - (a.comp?.outdated === true))
        setNodes(rows)
        setSelected(rows.filter((r) => r.comp?.outdated === true).map((r) => r.id))
      })
      .catch((e) => message.error(`加载节点失败: ${errMsg(e)}`))
      .finally(() => setLoading(false))
  }, [open, comp])

  const submit = async () => {
    if (!selected.length) {
      message.warning('请选择节点')
      return
    }
    // 保持列表顺序(灰度取第一台)
    const ids = nodes.filter((n) => selected.includes(n.id)).map((n) => n.id)
    setSubmitting(true)
    try {
      const res = await componentService.createBatch({
        component: comp.component,
        version: version || undefined,
        node_ids: ids,
        canary,
        force,
      })
      onStarted(res.data?.batch?.id)
    } catch (e) {
      message.error(errMsg(e, '创建批次失败'))
    } finally {
      setSubmitting(false)
    }
  }

  const columns = [
    { title: '节点', dataIndex: 'name', render: (v, r) => <span>{v} <Text type="secondary" style={{ fontSize: 12 }}>{r.ip}</Text></span> },
    {
      title: '当前版本',
      width: 160,
      render: (_, r) => {
        if (!r.comp) return <Tag>未知</Tag>
        if (!r.comp.active) return <Tag color="red">{r.comp.version || '?'}</Tag>
        return <Tag color={r.comp.outdated ? 'orange' : 'green'}>{r.comp.version || '?'}</Tag>
      },
    },
  ]

  return (
    <Modal
      title={`批量更新 · ${comp ? componentLabel(comp.component) : ''} → ${version || comp?.latest?.version || '最新'}`}
      open={open}
      onCancel={onClose}
      onOk={submit}
      okText={`更新 ${selected.length} 台`}
      okButtonProps={{ disabled: !selected.length || (!version && !comp?.latest) }}
      confirmLoading={submitting}
      width={620}
      destroyOnClose
    >
      {comp && !comp.latest && !version && (
        <Alert type="warning" showIcon message="未设置最新版本" style={{ marginBottom: 12 }} />
      )}
      <Table
        size="small"
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={nodes}
        pagination={false}
        scroll={{ y: 360 }}
        rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
      />
      <Collapse
        ghost
        size="small"
        style={{ marginTop: 8 }}
        items={[
          {
            key: 'adv',
            label: '高级',
            children: (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Space>
                  <span>版本</span>
                  <Select
                    allowClear
                    placeholder="最新"
                    style={{ width: 220 }}
                    value={version}
                    onChange={setVersion}
                    options={(comp?.releases || []).map((r) => ({
                      value: r.version,
                      label: r.is_latest ? `${r.version} (最新)` : r.version,
                    }))}
                  />
                </Space>
                <Checkbox checked={canary} onChange={(e) => setCanary(e.target.checked)}>
                  <Tooltip title="先更新第一台，观察通过后再滚动其余">灰度</Tooltip>
                </Checkbox>
                <Checkbox checked={force} onChange={(e) => setForce(e.target.checked)}>
                  <Tooltip title="忽略忙碌检查（如 EVM 有迁移/备份）">强制</Tooltip>
                </Checkbox>
              </Space>
            ),
          },
        ]}
      />
    </Modal>
  )
}

// ---------------- 页面 ----------------
const ComponentVersions = () => {
  const [components, setComponents] = useState([])
  const [loading, setLoading] = useState(false)
  const [scanning, setScanning] = useState(false)
  const [batches, setBatches] = useState([])
  const [batchComp, setBatchComp] = useState(null)
  const [progressId, setProgressId] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await componentService.list()
      setComponents(res.data?.components || [])
    } catch (e) {
      message.error(`加载失败: ${errMsg(e)}`)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadBatches = useCallback(async () => {
    try {
      const res = await componentService.listBatches()
      setBatches(res.data?.batches || [])
    } catch (e) {
      // 静默
    }
  }, [])

  useEffect(() => {
    load()
    loadBatches()
  }, [load, loadBatches])

  const scan = async () => {
    setScanning(true)
    try {
      const res = await componentService.scan()
      const d = res.data || {}
      const reg = d.registered || []
      const skipped = d.skipped || []
      const text = `新登记 ${reg.length}，已存在 ${d.existing || 0}，跳过 ${skipped.length}`
      if (skipped.length) {
        Modal.warning({
          title: text,
          width: 600,
          content: (
            <div style={{ maxHeight: 300, overflow: 'auto', fontSize: 12 }}>
              {skipped.map((s, i) => (
                <div key={i}><Text code>{s.path}</Text> {s.reason}</div>
              ))}
            </div>
          ),
        })
      } else {
        message.success(text)
      }
      load()
    } catch (e) {
      message.error(errMsg(e, '扫描失败'))
    } finally {
      setScanning(false)
    }
  }

  const setLatest = async (r) => {
    try {
      await componentService.setLatest(r.id)
      message.success(`${r.version} 已设为最新`)
      load()
    } catch (e) {
      message.error(errMsg(e))
    }
  }

  const saveNotes = async (r, notes) => {
    if ((r.notes || '') === notes) return
    try {
      await componentService.setNotes(r.id, notes)
      setComponents((prev) =>
        prev.map((c) => ({ ...c, releases: c.releases.map((x) => (x.id === r.id ? { ...x, notes } : x)) })),
      )
    } catch (e) {
      message.error(errMsg(e))
    }
  }

  const releaseColumns = [
    {
      title: '版本',
      dataIndex: 'version',
      render: (v, r) => (
        <Space size={4}>
          <Text strong={r.is_latest}>{v}</Text>
          {r.is_latest && <Tag color="green">最新</Tag>}
        </Space>
      ),
    },
    {
      title: 'SHA256',
      dataIndex: 'sha256',
      width: 130,
      render: (v) => (v ? <Text code copyable={{ text: v }} style={{ fontSize: 12 }}>{v.slice(0, 12)}</Text> : '—'),
    },
    { title: '大小', dataIndex: 'size', width: 90, render: formatSize },
    { title: '日期', dataIndex: 'created_at', width: 110, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '—') },
    { title: '节点', dataIndex: 'node_count', width: 60 },
    {
      title: '备注',
      dataIndex: 'notes',
      render: (v, r) => (
        <Paragraph
          style={{ margin: 0, fontSize: 12 }}
          editable={{ onChange: (val) => saveNotes(r, val), tooltip: '编辑备注' }}
          type={v ? undefined : 'secondary'}
        >
          {v || ''}
        </Paragraph>
      ),
    },
    {
      title: '',
      width: 90,
      render: (_, r) =>
        r.is_latest ? null : (
          <Popconfirm title={`将 ${r.version} 设为最新?`} onConfirm={() => setLatest(r)}>
            <Button size="small" type="link">设为最新</Button>
          </Popconfirm>
        ),
    },
  ]

  const columns = [
    {
      title: '组件',
      dataIndex: 'component',
      render: (v, r) => (
        <Space size={4}>
          <Text strong>{componentLabel(v)}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text>
          {r.version_hint && (
            <Tooltip title={r.version_hint}>
              <InfoCircleOutlined style={{ color: '#faad14' }} />
            </Tooltip>
          )}
        </Space>
      ),
    },
    {
      title: '最新版本',
      width: 160,
      render: (_, r) => (r.latest ? <Tag color="green">{r.latest.version}</Tag> : <Text type="secondary">—</Text>),
    },
    {
      title: '节点',
      width: 220,
      render: (_, r) => {
        const onLatest = r.latest ? r.releases.find((x) => x.id === r.latest.id)?.node_count || 0 : 0
        const outdated = r.latest ? Math.max(0, (r.node_count || 0) - onLatest - (r.unknown_count || 0)) : 0
        return (
          <Space size={4}>
            <Tooltip title="最新"><Tag color="green">{onLatest}</Tag></Tooltip>
            <Tooltip title="过期"><Tag color={outdated ? 'orange' : 'default'}>{outdated}</Tag></Tooltip>
            <Tooltip title="未知版本"><Tag>{r.unknown_count || 0}</Tag></Tooltip>
          </Space>
        )
      },
    },
    {
      title: '',
      width: 110,
      render: (_, r) => (
        <Button size="small" icon={<CloudSyncOutlined />} disabled={!r.latest && !r.releases.length} onClick={() => setBatchComp(r)}>
          批量更新
        </Button>
      ),
    },
  ]

  const batchColumns = [
    { title: '#', dataIndex: 'id', width: 60 },
    { title: '组件', dataIndex: 'component', render: componentLabel },
    { title: '版本', dataIndex: 'version' },
    { title: '节点', dataIndex: 'node_ids', width: 60, render: (v) => (v || []).length },
    {
      title: '状态',
      dataIndex: 'status',
      render: (v, r) => {
        const s = BATCH_STATUS[v] || { label: v, color: 'default' }
        return (
          <Space size={4}>
            <Tag color={s.color}>{s.label}</Tag>
            {v === 'running' && r.phase && <Text type="secondary" style={{ fontSize: 12 }}>{BATCH_PHASE[r.phase] || r.phase}</Text>}
          </Space>
        )
      },
    },
    { title: '时间', dataIndex: 'created_at', render: (v) => (v ? dayjs(v).format('MM-DD HH:mm') : '—') },
    { title: '', width: 60, render: (_, r) => <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setProgressId(r.id)}>查看</Button> },
  ]

  const expandedKeys = useMemo(() => components.filter((c) => c.releases?.length).map((c) => c.component), [components])

  return (
    <>
      <Card
        title={
          <Space>
            <span>组件版本</span>
            <Tooltip title={<span>发布目录：downloads/releases/&lt;组件&gt;/&lt;版本&gt;/&lt;文件&gt;</span>}>
              <QuestionCircleOutlined style={{ color: '#999' }} />
            </Tooltip>
          </Space>
        }
        extra={
          <Space>
            <Button icon={<ScanOutlined />} loading={scanning} onClick={scan}>扫描发布目录</Button>
            <Button icon={<ReloadOutlined />} onClick={() => { load(); loadBatches() }} />
          </Space>
        }
      >
        <Table
          size="small"
          rowKey="component"
          loading={loading}
          columns={columns}
          dataSource={components}
          pagination={false}
          expandable={{
            // key 变化时重新默认展开
            defaultExpandedRowKeys: expandedKeys,
            rowExpandable: (r) => !!r.releases?.length,
            expandedRowRender: (r) => (
              <Table size="small" rowKey="id" columns={releaseColumns} dataSource={r.releases} pagination={false} />
            ),
          }}
          key={expandedKeys.join(',')}
        />
      </Card>

      <Card size="small" title="最近批次" style={{ marginTop: 16 }}>
        <Table size="small" rowKey="id" columns={batchColumns} dataSource={batches} pagination={false} />
      </Card>

      <BatchUpdateModal
        comp={batchComp}
        open={!!batchComp}
        onClose={() => setBatchComp(null)}
        onStarted={(id) => {
          setBatchComp(null)
          loadBatches()
          if (id) setProgressId(id)
        }}
      />
      <BatchProgressModal
        batchId={progressId}
        open={!!progressId}
        onClose={() => {
          setProgressId(null)
          loadBatches()
          load()
        }}
        onChanged={loadBatches}
      />
    </>
  )
}

export default ComponentVersions
